import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  Form,
  Label,
  FormGroup,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  FormFeedback,
  FormText,
  Button,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useMutation } from "react-query";
import { useTranslation } from "react-i18next";
import jwtDecode from "jwt-decode";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";

import { useAuth } from "../context/auth";
import {
  login,
  verifyAccount,
  respondChallenge,
  resendVerificationCode,
} from "../api/actions/auth";
import { fetcher } from "../api/client";
import useQueryParams from "../hooks/useQuery";
import InputWithLoader from "./InputWithLoader";
import LoadingButton from "./LoadingButton";
import Alert from "./Alert";
import i18n from "../i18n";
import { redirectTo, checkTratoDomain } from "../utils";

const defaultValues = {
  email: "",
  password: "",
  twofactorcode: "",
};

const LoginSchema = Yup.object().shape({
  email: Yup.string().email("InvalidEmail").required("Required"),
  password: Yup.string().required("Required"),
  twofactorcode: Yup.string().length(6, "TwoFactorCodeLength"),
});

function LoginForm({
  error,
  errorMsg,
  formik,
  isSubmitting,
  onErrorClose,
  token,
  onResendVerificationCode,
  hideSignUpLink,
  onForgotPassword,
  onGoToSignup,
}) {
  const { t } = useTranslation("common");
  const history = useHistory();

  const handleGoToSignup = () => {
    if (onGoToSignup) return onGoToSignup();
    history.push("/signup");
  };

  return (
    <>
      <Form onSubmit={formik.handleSubmit} noValidate>
        <FormGroup className="mt-3">
          {/* <Label for="email">
            {t("forms.email")} <span className="text-accent">*</span>
          </Label> */}
          <InputGroup className="mb-2">
            <InputGroupAddon addonType="prepend">
              <InputGroupText>
                <FontAwesomeIcon icon="envelope" className="muted" />
              </InputGroupText>
            </InputGroupAddon>
            <InputWithLoader
              className="form-control border-left-0 rounded-right"
              disabled={!!token}
              id="email"
              aria-describedby="emailHelp"
              placeholder={t("forms.email")}
              type="email"
              name="email"
              loading={isSubmitting}
              onChange={formik.handleChange}
              value={formik.values.email}
              invalid={formik.errors.email && formik.touched.email}
            />
            <FormFeedback>{t(`errors.${formik.errors.email}`)}</FormFeedback>
          </InputGroup>
        </FormGroup>
        <FormGroup>
          {/* <Label for="password">
            {t("forms.password")} <span className="text-accent">*</span>
          </Label> */}
          <InputGroup className="mb-2">
            <InputGroupAddon addonType="prepend">
              <InputGroupText>
                <FontAwesomeIcon icon="lock" className="muted" />
              </InputGroupText>
            </InputGroupAddon>
            <InputWithLoader
              className="form-control border-left-0 rounded-right"
              disabled={!!token}
              id="password"
              placeholder={t("forms.password")}
              type="password"
              name="password"
              loading={isSubmitting}
              onChange={formik.handleChange}
              value={formik.values.password}
              invalid={formik.errors.password && formik.touched.password}
            />
            <FormFeedback>{t(`errors.${formik.errors.password}`)}</FormFeedback>
          </InputGroup>
          {!onForgotPassword && (
            <Link to="/forgot-password">
              <FormText color="muted">{t("auth.forgotPassword")}</FormText>
            </Link>
          )}
          {onForgotPassword && (
            <FormText color="muted" onClick={onForgotPassword}>
              {t("auth.forgotPassword")}
            </FormText>
          )}
        </FormGroup>
        <FormGroup hidden={!token}>
          {/* <Label for="twofactorcode">
            {t("forms.twofactorcode")} <span className="text-accent">*</span>
          </Label> */}
          <InputGroup className="mb-2">
            <InputGroupAddon addonType="prepend">
              <InputGroupText>
                <FontAwesomeIcon icon="lock" className="text-yellow" />
              </InputGroupText>
            </InputGroupAddon>
            <InputWithLoader
              className="form-control border-left-0 rounded-right"
              id="twofactorcode"
              placeholder={t("forms.twofactorcode")}
              type="text"
              name="twofactorcode"
              loading={isSubmitting}
              onChange={formik.handleChange}
              value={formik.values.twofactorcode}
              invalid={
                formik.errors.twofactorcode && formik.touched.twofactorcode
              }
            />
            <FormFeedback>
              {t(`errors.${formik.errors.twofactorcode}`)}
            </FormFeedback>
          </InputGroup>
        </FormGroup>
        <Alert
          primary="Error"
          isOpen={error}
          toggle={onErrorClose}
          secondary={t(`errors.${errorMsg}`)}
          icon="info"
          size="sm"
        />
        {errorMsg === "EmailNotVerified" && (
          <Button color="accent" onClick={onResendVerificationCode}>
            Reenviar código de verificación
          </Button>
        )}
        <div className="mt-4">
          <LoadingButton
            className="animate-up w-100 mb-3 rounded"
            color="accent-100"
            type="submit"
            loadingText={t("actions.submitting")}
            loading={isSubmitting}
          >
            {t("auth.login")}
          </LoadingButton>
          <hr class="hr-text" data-content="O" />
          {!hideSignUpLink && (
            <p>
              {t("auth.notRegistered")}{" "}
              <a href="#" onClick={handleGoToSignup}>
                {t("auth.createAccount")}
              </a>
            </p>
          )}
        </div>
      </Form>
    </>
  );
}

export function parseAuthResult(data, { hasInitialConfig = false } = {}) {
  if (!data) return {};
  if (!data.AuthenticationResult) return data;

  const authResult = data.AuthenticationResult;
  const idToken = jwtDecode(authResult.IdToken);

  return {
    accessToken: authResult.AccessToken,
    refreshToken: authResult.RefreshToken,
    idToken: authResult.IdToken,
    expiresIn: authResult.ExpiresIn,
    expiresAt: Date.now() + Number(authResult.ExpiresIn) * 1000,
    user: {
      _id: idToken.profile,
      givenName: idToken.given_name,
      familyName: idToken.family_name,
      name: idToken.name,
      email: idToken.email,
      verified: idToken.email_verified,
      cognitoId: idToken.sub,
      hasInitialConfig,
      //     organization: idToken['custom:organization']
    },
  };
}

function LoginFormContainer({
  payload = {},
  hideSignUpLink,
  onForgotPassword,
  onLoginSuccess,
  onGoToSignup,
}) {
  const history = useHistory();
  const [auth, setAuth] = useAuth();
  const [token, setToken] = useState(null);
  const [twoFactor, setTwoFactor] = useState(null);
  const queryString = useQueryParams();

  const onSuccess = async (result) => {
    const data = parseAuthResult(result.data, {
      hasInitialConfig: twoFactor?.hasInitialConfig,
    });
    console.log({ onLoginSuccess });

    const user = data.user ?? {};
    if (data.hasChallenge && data.challengeName === "SOFTWARE_TOKEN_MFA") {
      if (data.shouldRedirect) {
        window.location.href = `https://${data.domain}.trato.io/login?session=${data.challengeParameters.session}&username=${data.challengeParameters.userIdForSRP}&hasInitialConfig=${data.hasInitialConfig}&email=${formik.values.email}&type=two-factor-redirect`;
        return;
      }
      setTwoFactor({
        session: data.challengeParameters.session,
        username: data.challengeParameters.userIdForSRP,
        hasInitialConfig: data.hasInitialConfig,
      });
    } else if (
      data.hasChallenge &&
      data.challengeName === "NEW_PASSWORD_REQUIRED"
    ) {
      history.push(
        `/account/${data.challengeParameters.username}/change-password/${data.challengeParameters.session}?hasTmpPwd=true`
      );
    } else if (
      data.hasChallenge &&
      data.challengeName === "REDIRECT_CHALLENGE"
    ) {
      redirectTo(
        data.domain,
        data.challengeParameters.session,
        data.challengeParameters.username
      );
    } else if (data.twofactorToken) {
      setToken(data.twofactorToken);
    } else if (data.accessToken) {
      // const user = jwtDecode(data.jwt);
      // setAuth({
      //     token: data.jwt,
      //     user
      // })
      setAuth({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        idToken: data.idToken,
        expiresIn: data.expiresIn,
        expiresAt: Date.now() + Number(data.expiresIn) * 1000,
        user: data.user,
      });
      setTwoFactor(null);
      // redirectTo(data.domain, data.accessToken)
      i18n.changeLanguage(data.user?.language ?? i18n.language ?? "es");
      if (onLoginSuccess) {
        console.log({ onLoginSuccess, data });
        return await onLoginSuccess(data);
      }
      if (data.user?.hasInitialConfig) {
        if (
          data.publicTemplate?.name &&
          data.user?.updatedAt === data.user?.createdAt
        ) {
          return history.push(
            `/templates/catalog?search=${data.publicTemplate?.name}&searchExact=true`
          );
        }
        if (data?.isLawyer) return history.replace("/lawyers");
        return history.replace("/");
      }
      history.replace("/account-setup");
    }
  };

  const { mutate: mutateLogin, ...loginState } = useMutation(
    (payload) => fetcher(login(payload)),
    { onSuccess }
  );
  const { mutate: mutateLoginToken, ...loginTokenState } = useMutation(
    (payload) => fetcher(respondChallenge(payload)),
    {
      onSuccess,
      onError: (error) => {
        if (error.message === "TokenNotFound") {
          setToken(null);
          formik.reset();
        }
      },
    }
  );
  const { mutate: mutateResendVerificationCode } = useMutation(
    (payload) => fetcher(resendVerificationCode(payload)),
    {
      onSuccess: () => {
        toast.success("Te hemos reenviado el código");
      },
      onError: (error) => {},
    }
  );

  const handleSubmit = async (formValues, actions) => {
    const onMutate = () => {
      actions.setSubmitting(true);
    };
    const onSettled = () => {
      actions.setSubmitting(false);
    };
    formValues.payload = payload;
    if (twoFactor) {
      if (!formValues.twofactorcode) {
        actions.setFieldError("twofactorcode", "Required");
        actions.setFieldTouched("twofactorcode", true, false);
      } else {
        mutateLoginToken(
          {
            type: "SOFTWARE_TOKEN_MFA",
            code: formValues.twofactorcode,
            session: twoFactor.session,
            username: twoFactor.username,
          },
          {
            onMutate,
            onSettled,
          }
        );
      }
    } else {
      mutateLogin(formValues, {
        onMutate,
        onSettled,
      });
    }
  };

  const handleAlertClose = () => {
    loginTokenState.reset();
    loginState.reset();
  };

  const formik = useFormik({
    initialValues: {
      defaultValues,
      email: queryString.get("email") ?? "",
      password:
        queryString.get("type") === "two-factor-redirect"
          ? "dummypassword"
          : "",
    },
    validationSchema: LoginSchema,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    const session = queryString.get("session");
    const username = queryString.get("username");
    const hasInitialConfig = queryString.get("hasInitialConfig");

    if (session) {
      setTwoFactor({
        session,
        username,
        hasInitialConfig,
      });
    }
  }, []);

  const handleResendVerificationCode = () => {
    mutateResendVerificationCode({ username: formik.values.email });
  };

  return (
    <LoginForm
      error={loginState.isError || loginTokenState.isError}
      onErrorClose={handleAlertClose}
      errorMsg={loginState.error?.message ?? loginTokenState.error?.message}
      onSubmit={handleSubmit}
      token={twoFactor}
      formik={formik}
      isSubmitting={loginState.isLoading || loginTokenState.isLoading}
      hideSignUpLink={hideSignUpLink}
      onResendVerificationCode={handleResendVerificationCode}
      onForgotPassword={onForgotPassword}
      onGoToSignup={onGoToSignup}
    />
  );
}

export default LoginFormContainer;
